import { supabase } from './client';

// ============================================================
// TEACHER AUTHENTICATION (Google OAuth)
// ============================================================

/**
 * Sign in teacher with Google OAuth
 * Enhanced with better error handling and logging for deployment issues
 */
export async function signInTeacherWithGoogle() {
  try {
    const currentOrigin = window.location.origin;
    const redirectUrl = `${currentOrigin}/teacher/dashboard`;
    
    console.log('Initiating Google OAuth sign-in');
    console.log('Redirect URL:', redirectUrl);
    console.log('Current Origin:', currentOrigin);
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        }
      }
    });

    if (error) {
      console.error('OAuth sign-in error:', error);
      throw error;
    }
    
    console.log('OAuth initialization successful, user will be redirected to Google');
    return data;
  } catch (error) {
    console.error('Failed to initiate Google OAuth:', error);
    throw error;
  }
}

/**
 * Verify OAuth session after redirect
 * Call this when redirected back from Google to ensure session is properly established
 */
export async function verifyOAuthSession() {
  try {
    console.log('Verifying OAuth session after redirect...');
    
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('Error verifying session:', error);
      throw error;
    }
    
    if (!session?.user) {
      console.log('No session found after OAuth redirect');
      return null;
    }
    
    console.log('OAuth session verified for user:', session.user.id);
    return session;
  } catch (error) {
    console.error('Session verification failed:', error);
    throw error;
  }
}

/**
 * Create or update teacher profile after OAuth
 */
export async function ensureTeacherProfile(userId: string, userData: {
  full_name: string;
  avatar_url?: string;
  email?: string;
}) {
  // Check if profile exists
  const { data: existing } = await supabase
    .from('profiles')
    .select('id')
    .eq('id', userId)
    .maybeSingle();

  if (existing) {
    // Update existing profile
    const updateData: any = {
      full_name: userData.full_name,
      avatar_url: userData.avatar_url || null,
    };
    
    if (userData.email) {
      updateData.email = userData.email;
    }

    const { data, error } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('id', userId)
      .select()
      .maybeSingle();

    if (error) throw error;
    return data;
  } else {
    // Create new profile
    const insertData: any = {
      id: userId,
      role: 'teacher',
      full_name: userData.full_name,
      avatar_url: userData.avatar_url || null,
    };
    
    if (userData.email) {
      insertData.email = userData.email;
    }

    const { data, error } = await supabase
      .from('profiles')
      .insert(insertData)
      .select()
      .maybeSingle();

    if (error) throw error;
    return data;
  }
}

/**
 * Sign out current user
 */
export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

// ============================================================
// STUDENT AUTHENTICATION (Anonymous with Session Code)
// ============================================================

/**
 * Validate session code and check if session is active
 */
export async function validateSessionCode(sessionCode: string) {
  const { data, error } = await supabase
    .from('sessions')
    .select(`
      id,
      lesson_id,
      session_code,
      mode,
      status,
      current_slide,
      lessons (
        id,
        title,
        mode
      )
    `)
    .eq('session_code', sessionCode.toUpperCase())
    .eq('status', 'active')
    .maybeSingle();

  if (error) {
    if (error.code === 'PGRST116') {
      throw new Error('Invalid session code or session has ended');
    }
    throw error;
  }

  return data;
}

/**
 * Join session as a student (create participant record)
 */
export async function joinSessionAsStudent(sessionId: string, studentName: string) {
  // Check if student already joined this session
  const { data: existing } = await supabase
    .from('session_participants')
    .select('id, student_name')
    .eq('session_id', sessionId)
    .eq('student_name', studentName)
    .maybeSingle();

  if (existing) {
    // Update last_active
    const { data, error } = await supabase
      .from('session_participants')
      .update({ last_active: new Date().toISOString() })
      .eq('id', existing.id)
      .select()
      .maybeSingle();

    if (error) throw error;
    return data;
  }

  // Create new participant record
  const { data, error } = await supabase
    .from('session_participants')
    .insert({
      session_id: sessionId,
      student_name: studentName,
      progress_percentage: 0,
      last_active: new Date().toISOString()
    })
    .select()
    .maybeSingle();

  if (error) {
    if (error.code === '23505') {
      throw new Error('You have already joined this session');
    }
    throw error;
  }

  return data;
}

/**
 * Create anonymous student session
 * This creates a temporary auth session for the student
 */
export async function createStudentSession(studentName: string, sessionId: string, sessionCode?: string) {
  // Clear any stale Supabase auth session (e.g. from a previous teacher login)
  // so the client falls back to the anon key for REST and Realtime calls.
  // Without this, an expired teacher JWT causes 401 errors on student operations.
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      await supabase.auth.signOut();
    }
  } catch {
    // Ignore sign-out errors — the goal is to clear stale tokens
  }

  // Store student name in localStorage for client-side access control
  localStorage.setItem('student_name', studentName);
  localStorage.setItem('student_session_id', sessionId);
  if (sessionCode) {
    localStorage.setItem('student_session_code', sessionCode);
  }

  return {
    studentName,
    sessionId,
    sessionCode,
    isStudent: true
  };
}

/**
 * Get current student session from localStorage
 */
export function getStudentSession() {
  const studentName = localStorage.getItem('student_name');
  const sessionId = localStorage.getItem('student_session_id');
  const sessionCode = localStorage.getItem('student_session_code');
  
  if (!studentName || !sessionId) return null;
  
  return {
    studentName,
    sessionId,
    sessionCode,
    isStudent: true
  };
}

/**
 * Clear student session
 */
export function clearStudentSession() {
  localStorage.removeItem('student_name');
  localStorage.removeItem('student_session_id');
  localStorage.removeItem('student_session_code');
}

/**
 * Check if current user is a student
 */
export function isStudentSession(): boolean {
  return !!getStudentSession();
}

// ============================================================
// SESSION MANAGEMENT
// ============================================================

/**
 * Get current user type (teacher or student)
 */
export async function getCurrentUserType(): Promise<'teacher' | 'student' | null> {
  // Check if student session exists
  if (isStudentSession()) {
    return 'student';
  }

  // Check if authenticated teacher
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.user) {
    const { data: profile } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', session.user.id)
      .maybeSingle();

    return profile?.role === 'teacher' ? 'teacher' : null;
  }

  return null;
}